import React, { useState, useEffect } from 'react';
import { Save, Upload, X, AlertCircle, CheckCircle, Camera, Clock, Globe } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const BasicInformation = () => {
  const [formData, setFormData] = useState({
    store_name: '',
    owner_name: '',
    owner_phone: '',
    contact_number: '',
    contact_email: '',
    business_address: '',
    business_registration: '',
    tax_id: '',
    store_description: ''
  });

  const [businessHours, setBusinessHours] = useState({
    monday: { open: '09:00', close: '18:00', closed: false },
    tuesday: { open: '09:00', close: '18:00', closed: false },
    wednesday: { open: '09:00', close: '18:00', closed: false },
    thursday: { open: '09:00', close: '18:00', closed: false },
    friday: { open: '09:00', close: '18:00', closed: false },
    saturday: { open: '09:00', close: '18:00', closed: false },
    sunday: { closed: true, open: '', close: '' }
  });

  const [socialMedia, setSocialMedia] = useState({
    facebook: '',
    instagram: '',
    twitter: '',
    youtube: '',
    linkedin: ''
  });

  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  

  
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  const getMultipartHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  useEffect(() => {
    loadBasicInfo();
  }, []);



  const loadBasicInfo = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/basic-info`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        const info = data.data;
        
        setFormData({
          store_name: info.store_name || '',
          owner_name: info.owner_name || '',
          owner_phone: info.owner_phone || '',
          contact_number: info.contact_number || '',
          contact_email: info.contact_email || '',
          business_address: info.business_address || '',
          business_registration: info.business_registration || '',
          tax_id: info.tax_id || '',
          store_description: info.store_description || ''
        });

        if (info.business_hours) {
          setBusinessHours(info.business_hours);
        }

        if (info.social_media) {
          setSocialMedia(info.social_media);
        }

        // Fixed logo URL handling
        if (info.logo_url) {
          setCurrentLogo(info.logo_url);
        } else {
          setCurrentLogo(null);
        }
      } else {
        console.error('Failed to load basic info:', response.status);
        setError('Failed to load basic information');
      }
    } catch (error) {
      console.error('Error loading basic info:', error);
      setError('Failed to load basic information');
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleBusinessHoursChange = (day, field, value) => {
    setBusinessHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setSocialMedia(prev => ({
      ...prev,
      [platform]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      
      // Validate file size (1MB = 1024 * 1024 bytes)
      if (file.size > 1024 * 1024) {
        setError('Logo file size must be less than 1MB');
        e.target.value = '';
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (JPG, PNG, GIF, WebP)');
        e.target.value = '';
        return;
      }

      // Clean up previous preview URL
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }

      setLogo(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      setError('');
    }
  };

  const removeLogo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/logo`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setCurrentLogo(null);
        setLogo(null);
        
        // Clean up preview URL
        if (logoPreview) {
          URL.revokeObjectURL(logoPreview);
          setLogoPreview(null);
        }
        
        // Clear file input
        const fileInput = document.getElementById('logo-upload');
        if (fileInput) {
          fileInput.value = '';
        }
        
        setSuccess('Logo removed successfully');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to remove logo');
      }
    } catch (error) {
      setError('Failed to remove logo');
    }
  };

  const validateForm = () => {
    if (!formData.store_name.trim()) {
      setError('Store name is required');
      return false;
    }
    if (!formData.owner_name.trim()) {
      setError('Owner name is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaveLoading(true);
    setError('');
    setSuccess('');

    try {
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });

      // Add JSON fields as strings
      submitData.append('business_hours', JSON.stringify(businessHours));
      submitData.append('social_media', JSON.stringify(socialMedia));

      // Add logo if selected
      if (logo) {
        submitData.append('store_logo', logo);
      }

      const response = await fetch(`${API_BASE_URL}/admin/basic-info`, {
        method: 'PUT',
        headers: getMultipartHeaders(),
        body: submitData
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Basic information updated successfully!');
        
        // If logo was uploaded and we get a logo URL back
        if (data.logo_url) {
          setCurrentLogo(data.logo_url);
          // Clean up preview
          if (logoPreview) {
            URL.revokeObjectURL(logoPreview);
            setLogoPreview(null);
          }
          setLogo(null);
        }
        
        // Clear file input
        const fileInput = document.getElementById('logo-upload');
        if (fileInput) {
          fileInput.value = '';
        }
        
      } else {
        setError(data.error || 'Failed to update basic information');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError('Network error occurred. Please check your connection and try again.');
    }
    setSaveLoading(false);
  };

  // Get the correct logo URL
  const getLogoUrl = () => {
    if (logoPreview) {
      return logoPreview; // Show preview of newly selected file
    }
    if (currentLogo) {
      return `${API_BASE_URL}/uploads/logos/${currentLogo}?t=${Date.now()}`;
    }
    return null;
  };

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  // Cleanup effect for preview URL
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Basic Information</h1>
        <button
          onClick={handleSubmit}
          disabled={saveLoading}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saveLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>{saveLoading ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
          <button
            onClick={() => setError('')}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
          <span className="text-green-700 text-sm">{success}</span>
          <button
            onClick={() => setSuccess('')}
            className="ml-auto text-green-500 hover:text-green-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
        {/* Store Information */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Store Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Store Name *
              </label>
              <input
                type="text"
                name="store_name"
                value={formData.store_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Owner Name *
              </label>
              <input
                type="text"
                name="owner_name"
                value={formData.owner_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Store Description
              </label>
              <textarea
                name="store_description"
                value={formData.store_description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe your store..."
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Owner Phone
              </label>
              <input
                type="tel"
                name="owner_phone"
                value={formData.owner_phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Number
              </label>
              <input
                type="tel"
                name="contact_number"
                value={formData.contact_number}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email
              </label>
              <input
                type="email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Address
              </label>
              <textarea
                name="business_address"
                value={formData.business_address}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Full business address..."
              />
            </div>
          </div>
        </div>

        {/* Logo Section */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Store Logo</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                {getLogoUrl() ? (
                  <div className="relative group">
                    <img
                      src={getLogoUrl()}
                      alt="Store Logo"
                      className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                      onError={(e) => {
                        console.error('Logo failed to load:', e.target.src);
                        // Show error state
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      onLoad={(e) => {
                        e.target.style.display = 'block';
                        e.target.nextSibling.style.display = 'none';
                      }}
                    />
                    {/* Error fallback */}
                    <div className="w-32 h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hidden flex-col items-center justify-center">
                      <Camera className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-xs text-gray-500">Failed to load</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm opacity-0 group-hover:opacity-100"
                      title="Remove logo"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-gray-400 transition-colors">
                    <Camera className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-xs text-gray-500">No logo</span>
                  </div>
                )}
              </div>
              
              <div className="flex-1 space-y-3">
                <div>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleLogoChange}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 cursor-pointer transition-colors text-sm font-medium"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Upload Logo</span>
                  </label>
                </div>
                
                <div className="text-xs text-gray-500 space-y-1">
                  <p>• Maximum file size: 1MB</p>
                  <p>• Supported formats: JPG, PNG, GIF, WebP</p>
                  <p>• Recommended size: 400x400px or square ratio</p>
                </div>
                
                {logo && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-2">
                    <p className="text-xs text-green-700 font-medium">
                      ✓ Selected: {logo.name}
                    </p>
                    <p className="text-xs text-green-600">
                      Size: {(logo.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                )}
                
                {currentLogo && !logoPreview && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                    <p className="text-xs text-blue-700 font-medium">
                      Current logo uploaded
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Registration Number
              </label>
              <input
                type="text"
                name="business_registration"
                value={formData.business_registration}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tax ID / GST Number
              </label>
              <input
                type="text"
                name="tax_id"
                value={formData.tax_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-center space-x-2">
                  <div className="text-blue-600 font-medium">Currency:</div>
                  <div className="text-blue-800">Indian Rupee (₹)</div>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <div className="text-blue-600 font-medium">Timezone:</div>
                  <div className="text-blue-800">India Standard Time (IST)</div>
                </div>
              </div>
            </div>
          </div>
        </div>



        {/* Business Hours */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Business Hours
          </h2>
          <div className="space-y-3">
            {daysOfWeek.map(day => (
              <div key={day.key} className="flex items-center space-x-4">
                <div className="w-24">
                  <span className="text-sm font-medium text-gray-700">{day.label}</span>
                </div>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={businessHours[day.key].closed}
                    onChange={(e) => handleBusinessHoursChange(day.key, 'closed', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 mr-2"
                  />
                  <span className="text-sm text-gray-600">Closed</span>
                </label>

                {!businessHours[day.key].closed && (
                  <>
                    <input
                      type="time"
                      value={businessHours[day.key].open}
                      onChange={(e) => handleBusinessHoursChange(day.key, 'open', e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                    />
                    <span className="text-gray-500">to</span>
                    <input
                      type="time"
                      value={businessHours[day.key].close}
                      onChange={(e) => handleBusinessHoursChange(day.key, 'close', e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Social Media */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Globe className="h-5 w-5 mr-2" />
            Social Media Links
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.keys(socialMedia).map(platform => (
              <div key={platform}>
                <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                  {platform} URL
                </label>
                <input
                  type="url"
                  value={socialMedia[platform]}
                  onChange={(e) => handleSocialMediaChange(platform, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={`https://${platform}.com/yourstore`}
                />
              </div>
            ))}
          </div>
        </div>
      </form>
    </div>
  );
};

export default BasicInformation;